import React from 'react';
import { MessagingProtocol, MockService } from '../../../../../../types/rest/mockServiceTypes';
import RunnerActiveSwitch from './RunnerActiveSwitch';
import { MockParameterState } from '../../MockParameters';
import MessagingRunnerTabs from './MessagingRunnerTabs';
import HttpRunnerTabs from './HttpRunnerTabs';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../../store/rootReducer';
import {
  updateMessagingRunnerTab,
  updateRunnerTab,
} from '../../../../../../store/mockServices/mockServiceDetail/mockServiceDetailActions';
import RecordingButton from '../Messaging/Recordings/RecordingButton';
import { startRecordingThunk, stopRecordingThunk } from '../../../../../../store/mockServices/mockServiceActions';
import HelpTooltip from '../../../../../../components/overlays/helpTooltip/HelpTooltip';
import RunnersButtonGroup from '../../../../../../components/ui/ButtonGroups/RunnersButtonGroup';
import { isTemplate } from '../../../../../../utils/miscUtil';
import { updateTemplateRunnerTab } from 'store/mockServiceTemplates/mockServiceTemplateDetail/mockServiceTemplateDetailActions';

interface Props {
  mockService: MockService;
  onUpdate: (updated: MockParameterState, debounce?: boolean) => void;
  disabled: boolean;
}

const MockServiceRunners = ({ mockService, onUpdate, disabled }: Props) => {
  const activeTab = useSelector((state: ApplicationState) => {
    if (isTemplate(mockService)) {
      return state.mockServiceTemplateDetail.runnerTab;
    } else {
      return state.mockServiceDetails.runnerTab;
    }
  });
  const dispatch = useDispatch();

  const getRecordingBtn = (type: 'HTTP' | 'MESSAGING') => {
    if (type === 'MESSAGING' && !mockService.isRecorderModeActive) {
      return null;
    } else if (type === 'HTTP' && !mockService.isHttpRecorderModeActive) {
      return null;
    }

    const currentStatus = type === 'HTTP' ? mockService.httpRecorderStatus : mockService.recorderStatus;

    return (
      <div className={'mb-3'}>
        <RecordingButton
          disabled={disabled || mockService.status !== 'RUNNING' || currentStatus === 'INACTIVE'}
          onClick={() => {
            if (currentStatus === 'RUNNING') {
              dispatch(stopRecordingThunk(mockService, type));
            } else {
              dispatch(startRecordingThunk(mockService, type));
            }
          }}
          status={currentStatus}
        />
        {(disabled || mockService.status !== 'RUNNING' || currentStatus === 'INACTIVE') && (
          <div className="text-muted small mt-2">Run the Virtual Service to enable recording.</div>
        )}
      </div>
    );
  };

  const getHttpRunnerContent = () => {
    return (
      <>
        <div>
          <>
            <RunnerActiveSwitch
              isActive={mockService.httpRunnerEnabled}
              label={'Runner Active'}
              onChange={isActive => {
                const update = { ...mockService };
                update.httpRunnerEnabled = isActive;
                onUpdate(update);
              }}
              disabled={false}
            />
            <span className="ml-5"> </span>
          </>
          {/* Recording Mode */}
          {!isTemplate(mockService) && (
            <RunnerActiveSwitch
              isActive={mockService.isHttpRecorderModeActive}
              label={
                <span>
                  Recording Mode <HelpTooltip fontSize={16} id={'http-recording-help'} />
                </span>
              }
              onChange={(isActive: boolean) => {
                const update = { ...mockService };
                update.isHttpRecorderModeActive = isActive;
                onUpdate(update);
              }}
              disabled={!mockService.httpRunnerEnabled}
            />
          )}
        </div>
        {getRecordingBtn('HTTP')}
        <HttpRunnerTabs mockService={mockService} disabled={disabled || !mockService.httpRunnerEnabled} />
      </>
    );
  };

  const onSwitchOnRecordingModeHandler = (isActive: boolean) => {
    setRecordingMode(isActive);
  };

  const setRecordingMode = (isActive: boolean) => {
    //Go to Messaging Configuration screen
    dispatch(updateMessagingRunnerTab('main'));

    const update = { ...mockService };
    update.isRecorderModeActive = isActive;
    onUpdate(update);
  };

  const getMessagingRunnerContent = () => {
    return (
      <div>
        <div className={'mb-3'}>
          {/* Messaging runner active */}
          <RunnerActiveSwitch
            isActive={mockService.messagingRunnerEnabled}
            label={'Runner Active'}
            onChange={(isActive: boolean) => {
              const update = { ...mockService };
              update.messagingRunnerEnabled = isActive;

              //Set default messaging protocol
              if (isActive && !update.messagingProtocol) {
                update.messagingProtocol = MessagingProtocol.IBM_MQ9_NATIVE;
              }

              onUpdate(update);
            }}
            disabled={false}
          />

          {/* Recording Mode */}
          {!isTemplate(mockService) && (
            <RunnerActiveSwitch
              className={'ml-5'}
              isActive={mockService.isRecorderModeActive}
              label={
                <span>
                  Recording Mode <HelpTooltip fontSize={16} id={'message-recording-help'} />
                </span>
              }
              onChange={(isActive: boolean) => onSwitchOnRecordingModeHandler(isActive)}
              disabled={!mockService.messagingRunnerEnabled}
            />
          )}
        </div>

        {getRecordingBtn('MESSAGING')}

        <MessagingRunnerTabs disabled={disabled || !mockService.messagingRunnerEnabled} mockService={mockService} />
      </div>
    );
  };

  return (
    <>
      <RunnersButtonGroup
        activeRunner={activeTab}
        onRunnerChanged={activeRunner => {
          if (isTemplate(mockService)) {
            dispatch(updateTemplateRunnerTab(activeRunner));
          } else {
            dispatch(updateRunnerTab(activeRunner));
          }
        }}
        className={'mb-4'}
      />

      {activeTab === 'httpRunner' && <div>{getHttpRunnerContent()}</div>}
      {activeTab === 'messagingRunner' && <div>{getMessagingRunnerContent()}</div>}
    </>
  );
};

export default MockServiceRunners;
